import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { maskTelefone, maskCep, maskCpfCnpj, formatNomeProprio, trimEspacos } from '../utils/masks';

const emptyForm = {
  nome: '', telefone: '', email: '', cpfCnpj: '',
  cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', estado: 'SP', observacoes: '',
};

export default function ClientesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  const { data: clientes = [], isLoading } = useQuery({
    queryKey: ['clientes', search],
    queryFn: () => api.get('/clientes', { params: search ? { search } : undefined }).then(r => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: (data: any) => editId ? api.put(`/clientes/${editId}`, data) : api.post('/clientes', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clientes'] }); setShowForm(false); },
  });

  function openNew() { setForm(emptyForm); setEditId(null); setShowForm(true); }
  function openEdit(c: any) {
    setForm({
      ...c,
      telefone: maskTelefone(c.telefone ?? ''),
      cpfCnpj: maskCpfCnpj(c.cpfCnpj ?? ''),
      cep: maskCep(c.cep ?? ''),
      cidade: c.cidade ?? '',
    });
    setEditId(c.id);
    setShowForm(true);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Clientes</h1>
        <button onClick={openNew} className="btn-primary">+ Novo</button>
      </div>

      <input className="input" placeholder="Buscar por nome, telefone ou email..." value={search} onChange={e => setSearch(e.target.value)} />

      {isLoading ? <div className="text-center py-8 text-gray-400">Carregando...</div> : (
        <div className="space-y-2">
          {clientes.map((c: any) => (
            <div key={c.id} onClick={() => openEdit(c)} className="card cursor-pointer hover:shadow-md">
              <div className="font-medium">{c.nome}</div>
              <div className="text-sm text-gray-500 flex gap-3 mt-1">
                <span>📞 {c.telefone}</span>
                {c.email && <span>✉️ {c.email}</span>}
              </div>
              {c.cidade && <div className="text-xs text-gray-400 mt-1">📍 {c.cidade}/{c.estado}</div>}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-end md:items-center justify-center">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full md:max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-semibold">{editId ? 'Editar Cliente' : 'Novo Cliente'}</h3>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div className="p-4 space-y-3">
              {([
                { label: 'Nome *', key: 'nome', type: 'text', placeholder: 'Nome completo' },
                { label: 'Telefone *', key: 'telefone', type: 'tel', placeholder: '(11) 99999-9999', mask: maskTelefone, numeric: true, maxLength: 15 },
                { label: 'Email', key: 'email', type: 'email', placeholder: '' },
                { label: 'CPF/CNPJ', key: 'cpfCnpj', type: 'text', placeholder: '000.000.000-00', mask: maskCpfCnpj, numeric: true, maxLength: 18 },
                { label: 'CEP', key: 'cep', type: 'text', placeholder: '00000-000', mask: maskCep, numeric: true, maxLength: 9 },
                { label: 'Endereço', key: 'logradouro', type: 'text', placeholder: 'Rua, Av...', mask: formatNomeProprio, onBlur: trimEspacos },
                { label: 'Número', key: 'numero', type: 'text', placeholder: '' },
                { label: 'Complemento', key: 'complemento', type: 'text', placeholder: 'Apto, Bloco...', mask: formatNomeProprio, onBlur: trimEspacos },
                { label: 'Bairro', key: 'bairro', type: 'text', placeholder: '', mask: formatNomeProprio, onBlur: trimEspacos },
                { label: 'Cidade', key: 'cidade', type: 'text', placeholder: '', mask: formatNomeProprio, onBlur: trimEspacos },
              ] as { label: string; key: string; type: string; placeholder: string; mask?: (v: string) => string; onBlur?: (v: string) => string; numeric?: boolean; maxLength?: number }[]).map(f => (
                <div key={f.key}>
                  <label className="label">{f.label}</label>
                  <input
                    type={f.type}
                    className="input"
                    placeholder={f.placeholder}
                    inputMode={f.numeric ? 'numeric' : undefined}
                    maxLength={f.maxLength}
                    value={(form as any)[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: f.mask ? f.mask(e.target.value) : e.target.value })}
                    onBlur={f.onBlur ? e => setForm({ ...form, [f.key]: f.onBlur!(e.target.value) }) : undefined}
                  />
                </div>
              ))}
              <div>
                <label className="label">Observações</label>
                <textarea className="input resize-none" rows={2} value={form.observacoes} onChange={e => setForm({ ...form, observacoes: e.target.value })} />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowForm(false)} className="btn-secondary flex-1">Cancelar</button>
                <button
                  onClick={() => saveMutation.mutate(form)}
                  disabled={!form.nome || !form.telefone || saveMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
